import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',

        // New variants
        success:
          'border-transparent bg-green-500 text-white [a&]:hover:bg-green-600 dark:bg-green-600 dark:[a&]:hover:bg-green-700 focus-visible:ring-green-500/20',
        warning:
          'border-transparent bg-yellow-500 text-white [a&]:hover:bg-yellow-600 dark:bg-yellow-600 dark:[a&]:hover:bg-yellow-700 focus-visible:ring-yellow-500/20',
        info: 'border-transparent bg-blue-500 text-white [a&]:hover:bg-blue-600 dark:bg-blue-600 dark:[a&]:hover:bg-blue-700 focus-visible:ring-blue-500/20',
        muted: 'border-transparent bg-muted text-muted-foreground [a&]:hover:bg-muted/80',
        accent: 'border-transparent bg-accent text-accent-foreground [a&]:hover:bg-accent/90',
        ghost: 'border-transparent text-foreground [a&]:hover:bg-accent/50',

        // Outline variants
        'outline-destructive':
          'border-destructive/50 text-destructive [a&]:hover:bg-destructive/10 focus-visible:ring-destructive/20',
        'outline-success':
          'border-green-500/50 text-green-700 dark:text-green-400 [a&]:hover:bg-green-500/10 focus-visible:ring-green-500/20',
        'outline-warning':
          'border-yellow-500/50 text-yellow-700 dark:text-yellow-400 [a&]:hover:bg-yellow-500/10 focus-visible:ring-yellow-500/20',
        'outline-info':
          'border-blue-500/50 text-blue-700 dark:text-blue-400 [a&]:hover:bg-blue-500/10 focus-visible:ring-blue-500/20',
        'outline-muted': 'border-border text-muted-foreground [a&]:hover:bg-muted/50',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
